import { GENDER_OPTIONS } from '../../constants'
import type {
  PlayerDetailsFieldSection,
  PlayerDetailsSectionKey,
  PlayerDetailsTableSection,
} from '../types'
import {
  APPAREL_SIZE_OPTIONS,
  HEARD_ABOUT_US_OPTIONS,
  PREFERRED_POSITION_OPTIONS,
  SKILL_LEVEL_OPTIONS,
} from './fieldOptions'

/** Sections with editable fields, keyed for lookup by the renderer. */
export const PLAYER_DETAILS_FIELD_SECTIONS: Record<
  PlayerDetailsFieldSection['key'],
  PlayerDetailsFieldSection
> = {
  contact: {
    key: 'contact',
    label: 'Personal Information',
    fields: [
      { name: 'firstName', label: 'First Name', kind: 'text' },
      { name: 'lastName', label: 'Last Name', kind: 'text' },
      { name: 'email', label: 'Email', kind: 'text' },
      {
        name: 'genderId',
        label: 'Gender',
        kind: 'select',
        options: GENDER_OPTIONS,
      },
      { name: 'birthDate', label: 'Date of Birth', kind: 'date' },
      { name: 'phoneNumber', label: 'Phone Number', kind: 'phone' },
      {
        name: 'alternatePhoneNumber',
        label: 'Alternate Phone Number',
        kind: 'phone',
      },
      { name: 'heightFt', label: 'Height', kind: 'height' },
      { name: 'weight', label: 'Weight', kind: 'number', unit: 'lbs' },
    ],
  },
  emergency: {
    key: 'emergency',
    label: 'Emergency Contact',
    fields: [
      {
        name: 'emergencyContactName',
        label: 'Emergency Contact Name',
        kind: 'text',
      },
      {
        name: 'emergencyContactRelationship',
        label: 'Relationship',
        kind: 'text',
      },
      { name: 'emergencyContactPhone', label: 'Phone Number', kind: 'phone' },
    ],
  },
  experience: {
    key: 'experience',
    label: 'Previous Experience',
    fields: [
      { name: 'previousTeam', label: 'Previous Team', kind: 'text' },
      { name: 'previousDivision', label: 'Previous Division', kind: 'text' },
      {
        name: 'preferredPosition',
        label: 'Preferred Position',
        kind: 'select',
        options: PREFERRED_POSITION_OPTIONS,
      },
      {
        name: 'skillLevel',
        label: 'Skill Level',
        kind: 'select',
        options: SKILL_LEVEL_OPTIONS,
      },
      {
        name: 'jerseySize',
        label: 'Jersey Size',
        kind: 'select',
        options: APPAREL_SIZE_OPTIONS,
      },
      {
        name: 'shortSize',
        label: 'Short Size',
        kind: 'select',
        options: APPAREL_SIZE_OPTIONS,
      },
      {
        name: 'previousYearsPlayed',
        label: 'Previous Years Played',
        kind: 'text',
        locked: true,
      },
      {
        name: 'medicalConditions',
        label: 'Medical Conditions',
        kind: 'textarea',
        fullWidth: true,
      },
      {
        name: 'previousExperience',
        label: 'Previous Experience',
        kind: 'textarea',
        fullWidth: true,
      },
    ],
  },
  hockey: {
    key: 'hockey',
    label: 'Membership Information',
    fields: [
      { name: 'hockeyUsaNumber', label: 'USA Hockey Number', kind: 'text' },
      {
        name: 'hockeyCanadaNumber',
        label: 'Hockey Canada Number',
        kind: 'text',
      },
      {
        name: 'customMembershipNumber',
        label: 'Custom Membership Number',
        kind: 'text',
      },
      {
        name: 'leagueRegistrationNumber',
        label: 'League Registration Number',
        kind: 'text',
      },
    ],
  },
  preferences: {
    key: 'preferences',
    label: 'Team Preferences',
    fields: [
      { name: 'teamPreference', label: 'Team Preference', kind: 'text' },
      {
        name: 'teammateRequest',
        label: 'Friend / Teammate Request',
        kind: 'text',
      },
      {
        name: 'additionalComments',
        label: 'Additional Comments / Notes',
        kind: 'textarea',
        fullWidth: true,
      },
    ],
  },
  additional: {
    key: 'additional',
    label: 'Additional Information',
    fields: [
      { name: 'goalieFee', label: 'Goalie Fee', kind: 'text', adminOnly: true },
      {
        name: 'highestLevelOfPlay',
        label: 'Highest Level Of Organized Play',
        kind: 'text',
        fullWidth: true,
      },
      {
        name: 'howDidYouHear',
        label: 'How Did You Hear About Us',
        kind: 'select',
        options: HEARD_ABOUT_US_OPTIONS,
      },
      { name: 'preferredLanguage', label: 'Preferred Language', kind: 'text' },
      { name: 'emailConsent', label: 'Email Consent', kind: 'switch' },
      {
        name: 'isFreeAgent',
        label: 'Is Free Agent',
        kind: 'switch',
        adminOnly: true,
      },
      {
        name: 'isFlagged',
        label: 'Flag',
        kind: 'switch',
        adminOnly: true,
        playerDetailsOnly: true,
      },
    ],
  },
  blacklist: {
    key: 'blacklist',
    label: 'Blacklist',
    fields: [
      { name: 'isBlacklisted', label: 'Blacklisted', kind: 'switch' },
      {
        name: 'blacklistedAt',
        label: 'Blacklisted Date',
        kind: 'date',
        locked: true,
      },
      {
        name: 'blacklistComments',
        label: 'Blacklist Comments',
        kind: 'textarea',
        fullWidth: true,
      },
    ],
  },
  // The Edit Player form's Social Profiles, same fields and payload.
  social: {
    key: 'social',
    label: 'Social Profiles',
    fields: [
      { name: 'facebook', label: 'Facebook', kind: 'text' },
      { name: 'instagram', label: 'Instagram', kind: 'text' },
      { name: 'tiktok', label: 'TikTok', kind: 'text' },
      { name: 'youtube', label: 'YouTube', kind: 'text' },
    ],
  },
  notes: {
    key: 'notes',
    label: 'Notes',
    adminOnly: true,
    fields: [
      {
        name: 'notes',
        label: 'Notes',
        kind: 'textarea',
        fullWidth: true,
        playerDetailsOnly: true,
      },
      {
        name: 'registrationNotes',
        label: 'Registration Notes',
        kind: 'textarea',
        fullWidth: true,
        locked: true,
      },
    ],
  },
}

/** Sections that render a table. */
export const PLAYER_DETAILS_TABLE_SECTIONS: Record<
  PlayerDetailsTableSection['key'],
  PlayerDetailsTableSection
> = {
  family: {
    key: 'family',
    label: 'Family Members',
    emptyMessage: 'No family members linked.',
  },
  registrations: {
    key: 'registrations',
    label: 'Registrations',
    emptyMessage: 'No registrations yet.',
  },
  rosters: {
    key: 'rosters',
    label: 'Team Rosters',
    emptyMessage: 'Not on any team roster.',
  },
  suspensions: {
    key: 'suspensions',
    label: 'Suspensions',
    emptyMessage: 'No suspensions.',
  },
  waivers: {
    key: 'waivers',
    label: 'Waivers',
    emptyMessage: 'No waivers signed yet.',
  },
}

/**
 * Sidebar + content order. Follows the Edit Player forms' order for the
 * sections both have (Personal › Emergency › Family › Previous Experience ›
 * Team Preferences › Additional Information › Notes › Social Profiles ›
 * Membership); Blacklist,
 * which only Player Details has, closes the field sections before the tables.
 */
export const PLAYER_DETAILS_SECTION_ORDER: ReadonlyArray<PlayerDetailsSectionKey> =
  [
    'contact',
    'emergency',
    'family',
    'experience',
    'preferences',
    'additional',
    'notes',
    'social',
    'hockey',
    'blacklist',
    'registrations',
    'rosters',
    'suspensions',
    'waivers',
  ]
